import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { toastSuccess, toastWarning } from "@/utils/toast";
import { getStaffConfig, getStaffDetail, saveEmergency } from "@/api/staff";

const BRAND = "#FF6B35";
const BRAND_GRADIENT = "linear-gradient(135deg, #FF6B35, #FF9A5C)";

export default function EmergencyPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    emergency_contact: "",
    emergency_phone: "",
  });
  const [bgImage, setBgImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [detail, cfg] = await Promise.allSettled([
          getStaffDetail(),
          getStaffConfig(),
        ]);

        if (detail.status === "fulfilled" && detail.value) {
          const d = detail.value as any;
          setFormData({
            emergency_contact: d.emergency_contact || "",
            emergency_phone: d.emergency_phone || "",
          });
        }

        if (cfg.status === "fulfilled" && cfg.value) {
          const c = cfg.value as any;
          setBgImage(c.urgency_linkman_bg || "");
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  // 提交
  const submit = async () => {
    if (!formData.emergency_contact.trim()) return toastWarning("请输入紧急联系人姓名");
    if (!formData.emergency_phone.trim()) return toastWarning("请输入紧急联系人电话");
    if (!/^1[3-9]\d{9}$/.test(formData.emergency_phone)) return toastWarning("请输入正确的手机号码");

    setSubmitting(true);
    try {
      await saveEmergency(formData);
      toastSuccess("保存成功");
      setTimeout(() => navigate(-1), 800);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <i className="ri-loader-4-line text-2xl animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif" }}>
      {/* 顶部背景图（含导航栏 + 标题区） */}
      <div className="relative">
        {/* 背景图 */}
        <div className="absolute inset-0 h-[260px]">
          {bgImage ? (
            <img src={bgImage} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full" style={{ background: BRAND_GRADIENT }} />
          )}
          {/* 遮罩：保证文字可读性 */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.12) 42%, rgba(255,255,255,0) 100%)",
            }}
          />
          {/* 底部橙紫过渡（接近截图氛围） */}
          <div
            className="absolute inset-x-0 bottom-0 h-[160px]"
            style={{
              background:
                "linear-gradient(180deg, rgba(255,107,53,0) 0%, rgba(255,107,53,0.25) 35%, rgba(120,72,255,0.35) 100%)",
            }}
          />
        </div>

        {/* 导航栏（叠在背景图上） */}
        <div className="relative z-10 px-5" style={{ paddingTop: "calc(env(safe-area-inset-top,0px) + 12px)" }}>
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:scale-[0.98] shrink-0"
              style={{ background: "rgba(255,255,255,0.22)", border: "1px solid rgba(255,255,255,0.18)" }}
              aria-label="返回"
            >
              <i className="ri-arrow-left-line" style={{ fontSize: 20, color: "#fff" }} />
            </button>
            <span className="text-base font-bold text-white">紧急联系人</span>
            <div className="w-9 h-9" />
          </div>
        </div>

        {/* 标题区 */}
        <div className="relative z-10 px-[15px] pt-[18px] pb-[80px]">
          <div className="flex items-start justify-between gap-4 text-white">
            <div>
              <p className="text-[22px] font-bold leading-tight mb-2">管理紧急联系人</p>
              <p className="text-sm text-white/90">添加或修改紧急联系人请及时告知对方</p>
            </div>
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.16)" }}
              aria-hidden
            >
              <i className="ri-qr-scan-2-line" style={{ fontSize: 22, color: "rgba(255,255,255,0.9)" }} />
            </div>
          </div>
        </div>

        {/* 内容区域（卡片上移覆盖在背景图上） */}
        <div className="relative z-20 px-[15px] pb-[80px]" style={{ marginTop: -58 }}>
          <div className="bg-white rounded-[14px] shadow-[0_10px_30px_rgba(0,0,0,0.10)] px-[15px] pb-[20px]">
            {/* 联系人姓名 */}
            <div className="flex items-center border-b border-gray-100 border-t-0 border-l-0 border-r-0 py-[16px]">
              <span className="text-sm mr-[15px] font-bold text-gray-900">联系人姓名</span>
              <input
                value={formData.emergency_contact}
                onChange={(e) => setFormData((p) => ({ ...p, emergency_contact: e.target.value }))}
                placeholder="请输入联系人姓名"
                className="flex-1 w-[120px] outline-none text-sm bg-transparent"
              />
            </div>

            {/* 联系人电话 */}
            <div className="flex items-center border-b border-gray-100 border-t-0 border-l-0 border-r-0 py-[16px] mb-[22px]">
              <span className="text-sm mr-[15px] font-bold text-gray-900">联系人电话</span>
              <input
                type="tel"
                value={formData.emergency_phone}
                onChange={(e) => setFormData((p) => ({ ...p, emergency_phone: e.target.value }))}
                placeholder="请输入联系人电话"
                className="flex-1 w-[120px] outline-none text-sm bg-transparent"
              />
            </div>

            {/* 保存按钮 */}
            <button
              onClick={submit}
              disabled={submitting}
              className={`w-full py-3 rounded-[30px] text-sm font-semibold cursor-pointer active:scale-[0.98] transition-all ${submitting ? "opacity-50" : ""}`}
              style={{ background: BRAND_GRADIENT, color: "#fff" }}
            >
              {submitting ? "保存中..." : "保存"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
